/**
 * PD-15708 Q9 — the mock merchant partner.
 *
 * A fifth payment processor, `mock`, that never leaves the process. It exists
 * so the billing test program can produce every gateway condition the real
 * sandboxes will not: a genuine decline with a code, a card held for review,
 * a transient gateway error, an expired card, debit and prepaid funding, a
 * refused bank debit, settlement on request, and a gateway-side query. The
 * behaviour is chosen by the test instrument, the way Adyen's test cards
 * work, so a scenario reads "use the decline card" rather than "delete the
 * profile at the processor".
 *
 * This file is isomorphic on purpose: the server adapter decodes the token
 * it defines, the two add-card forms encode it, and the fixture generator's
 * `tokenize.mjs` mirrors the encoding (kept trivially small for that reason).
 *
 * A PAN never reaches the server even here. The browser maps the card number
 * to a behaviour profile, keeps only the last four and the BIN, and sends a
 * token that carries those and nothing else.
 */

/** How a mock instrument behaves when charged. */
export type MockInstrumentProfile =
	| 'approve'
	| 'decline'
	| 'review'
	| 'error'
	| 'expired'
	| 'nsf';

/** How a mock card is funded; decides whether a processing fee applies. */
export type MockCardFunding = 'credit' | 'debit' | 'prepaid';

export interface MockTestCardModel {
	number: string;
	profile: MockInstrumentProfile;
	funding: MockCardFunding;
	brand: 'Visa' | 'MasterCard' | 'Amex' | 'Discover';
	description: string;
}

export interface MockTestBankAccountModel {
	routingNumber: string;
	accountNumber: string;
	profile: MockInstrumentProfile;
	description: string;
}

/**
 * The published test cards. Numbers are Luhn-valid and deliberately distinct
 * from every real gateway's sandbox numbers so a scenario cannot be run
 * against the wrong processor by accident.
 */
export const MOCK_TEST_CARDS: ReadonlyArray<MockTestCardModel> = [
	{
		number: '4242424242424242',
		profile: 'approve',
		funding: 'credit',
		brand: 'Visa',
		description: 'Approves; credit card, surchargeable',
	},
	{
		number: '5555555555554444',
		profile: 'approve',
		funding: 'debit',
		brand: 'MasterCard',
		description: 'Approves; debit card, never surcharged',
	},
	{
		number: '6011111111111117',
		profile: 'approve',
		funding: 'prepaid',
		brand: 'Discover',
		description: 'Approves; prepaid card, never surcharged',
	},
	{
		number: '4000000000000002',
		profile: 'decline',
		funding: 'credit',
		brand: 'Visa',
		description: 'Vaults, then every charge is declined by the issuer',
	},
	{
		number: '4000000000000044',
		profile: 'review',
		funding: 'credit',
		brand: 'Visa',
		description: 'Vaults, then every charge is held for review',
	},
	{
		number: '4000000000000119',
		profile: 'error',
		funding: 'credit',
		brand: 'Visa',
		description:
			'Vaults, then the first charge on the saved card fails with a gateway error and the retry approves',
	},
	{
		number: '4000000000000069',
		profile: 'expired',
		funding: 'credit',
		brand: 'Visa',
		description: 'Vaults, then every charge is refused as an expired card',
	},
];

export const MOCK_TEST_BANK_ACCOUNTS: ReadonlyArray<MockTestBankAccountModel> =
	[
		{
			routingNumber: '110000000',
			accountNumber: '000123456789',
			profile: 'approve',
			description: 'Bank debit approves',
		},
		{
			routingNumber: '110000000',
			accountNumber: '000111111113',
			profile: 'nsf',
			description: 'Bank debit is returned for insufficient funds',
		},
	];

/** The dataDescriptor every mock token carries. */
export const MOCK_DATA_DESCRIPTOR = 'MOCK.TEST.TOKEN';

/** Prefix on every vault reference and transaction id the mock issues. */
export const MOCK_EXTERNAL_ID_PREFIX = 'mock_';

export interface MockCardTokenModel {
	instrument: 'card';
	profile: MockInstrumentProfile;
	funding: MockCardFunding;
	brand: string;
	lastFour: string;
	bin: string;
	/** MMYYYY or MM/YY as entered; the adapter normalises it. */
	expirationDate: string | null;
}

export interface MockBankTokenModel {
	instrument: 'ach';
	profile: MockInstrumentProfile;
	routingNumber: string;
	lastFour: string;
	accountType: string | null;
}

export type MockTokenModel = MockCardTokenModel | MockBankTokenModel;

const digitsOnly = (value: string): string => value.replace(/\D/g, '');

/** Look up a card number in the published table. Undefined = not a mock card. */
export function findMockTestCard(
	cardNumber: string,
): MockTestCardModel | undefined {
	const number = digitsOnly(cardNumber);
	return MOCK_TEST_CARDS.find((card) => card.number === number);
}

export function findMockTestBankAccount(
	routingNumber: string,
	accountNumber: string,
): MockTestBankAccountModel | undefined {
	const routing = digitsOnly(routingNumber);
	const account = digitsOnly(accountNumber);
	return MOCK_TEST_BANK_ACCOUNTS.find(
		(row) => row.routingNumber === routing && row.accountNumber === account,
	);
}

const toBase64Url = (json: string): string => {
	const bytes = new TextEncoder().encode(json);
	let binary = '';
	for (const byte of bytes) {
		binary += String.fromCharCode(byte);
	}
	const b64 =
		typeof btoa === 'function'
			? btoa(binary)
			: Buffer.from(binary, 'binary').toString('base64');
	return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (value: string): string => {
	const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
	const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
	const binary =
		typeof atob === 'function'
			? atob(padded)
			: Buffer.from(padded, 'base64').toString('binary');
	const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
	return new TextDecoder().decode(bytes);
};

/**
 * Build the opaque token for a mock card. Throws when the number is not in
 * the published table, so a typo cannot silently become an "approve" card.
 * The full number is used only to look up the profile and is not encoded.
 */
export function encodeMockCardToken(
	cardNumber: string,
	expirationDate: string | null | undefined,
): { dataDescriptor: string; dataValue: string } {
	const card = findMockTestCard(cardNumber);
	if (card == null) {
		throw new Error(
			'That card number is not a mock test card. Use one of the published mock test cards.',
		);
	}
	const number = digitsOnly(cardNumber);
	const token: MockCardTokenModel = {
		instrument: 'card',
		profile: card.profile,
		funding: card.funding,
		brand: card.brand,
		lastFour: number.slice(-4),
		bin: number.slice(0, 6),
		expirationDate: expirationDate ?? null,
	};
	return {
		dataDescriptor: MOCK_DATA_DESCRIPTOR,
		dataValue: toBase64Url(JSON.stringify(token)),
	};
}

export function encodeMockBankToken(
	routingNumber: string,
	accountNumber: string,
	accountType: string | null | undefined,
): { dataDescriptor: string; dataValue: string } {
	const account = findMockTestBankAccount(routingNumber, accountNumber);
	if (account == null) {
		throw new Error(
			'That bank account is not a mock test account. Use one of the published mock test accounts.',
		);
	}
	const token: MockBankTokenModel = {
		instrument: 'ach',
		profile: account.profile,
		routingNumber: digitsOnly(routingNumber),
		lastFour: digitsOnly(accountNumber).slice(-4),
		accountType: accountType ?? null,
	};
	return {
		dataDescriptor: MOCK_DATA_DESCRIPTOR,
		dataValue: toBase64Url(JSON.stringify(token)),
	};
}

const PROFILES: ReadonlyArray<string> = [
	'approve',
	'decline',
	'review',
	'error',
	'expired',
	'nsf',
];
const FUNDINGS: ReadonlyArray<string> = ['credit', 'debit', 'prepaid'];

/** Decode a mock token. Returns null for anything that is not one. */
export function decodeMockToken(
	dataDescriptor: string | null | undefined,
	dataValue: string | null | undefined,
): MockTokenModel | null {
	if (dataDescriptor !== MOCK_DATA_DESCRIPTOR || dataValue == null) {
		return null;
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(fromBase64Url(dataValue));
	} catch {
		return null;
	}
	if (parsed == null || typeof parsed !== 'object') {
		return null;
	}
	const token = parsed as Record<string, unknown>;
	if (
		typeof token['profile'] !== 'string' ||
		!PROFILES.includes(token['profile']) ||
		typeof token['lastFour'] !== 'string'
	) {
		return null;
	}
	if (token['instrument'] === 'card') {
		if (
			typeof token['funding'] !== 'string' ||
			!FUNDINGS.includes(token['funding'])
		) {
			return null;
		}
		return {
			instrument: 'card',
			profile: token['profile'] as MockInstrumentProfile,
			funding: token['funding'] as MockCardFunding,
			brand: typeof token['brand'] === 'string' ? token['brand'] : 'Visa',
			lastFour: token['lastFour'],
			bin: typeof token['bin'] === 'string' ? token['bin'] : '',
			expirationDate:
				typeof token['expirationDate'] === 'string'
					? token['expirationDate']
					: null,
		};
	}
	if (token['instrument'] === 'ach') {
		return {
			instrument: 'ach',
			profile: token['profile'] as MockInstrumentProfile,
			routingNumber:
				typeof token['routingNumber'] === 'string'
					? token['routingNumber']
					: '',
			lastFour: token['lastFour'],
			accountType:
				typeof token['accountType'] === 'string' ? token['accountType'] : null,
		};
	}
	return null;
}

/**
 * The vault reference the mock issues for a method. It carries the profile
 * and funding so a charge against a saved method knows how to behave without
 * any state beyond the payment method row: `mock_card_decline_credit_0002_<id>`.
 */
export function buildMockExternalId(
	token: MockTokenModel,
	uniqueSuffix: string,
): string {
	const funding = token.instrument === 'card' ? token.funding : 'ach';
	return `${MOCK_EXTERNAL_ID_PREFIX}${token.instrument}_${token.profile}_${funding}_${token.lastFour}_${uniqueSuffix}`;
}

export interface MockExternalIdModel {
	instrument: 'card' | 'ach';
	profile: MockInstrumentProfile;
	funding: MockCardFunding | 'ach';
	lastFour: string;
}

/** Read the behaviour back out of a vault reference. Null = not a mock id. */
export function parseMockExternalId(
	externalId: string | null | undefined,
): MockExternalIdModel | null {
	if (externalId == null || !externalId.startsWith(MOCK_EXTERNAL_ID_PREFIX)) {
		return null;
	}
	const parts = externalId.slice(MOCK_EXTERNAL_ID_PREFIX.length).split('_');
	const [instrument, profile, funding, lastFour] = parts;
	if (
		(instrument !== 'card' && instrument !== 'ach') ||
		profile == null ||
		!PROFILES.includes(profile) ||
		funding == null ||
		lastFour == null
	) {
		return null;
	}
	return {
		instrument,
		profile: profile as MockInstrumentProfile,
		funding: funding as MockCardFunding | 'ach',
		lastFour,
	};
}
